import { useState, useEffect, useRef } from 'react';

function getRemainingSeconds(targetDate: string): number {
  const target = new Date(targetDate).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.floor((target - Date.now()) / 1000));
}

function formatSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (hours > 0) {
    const hh = String(hours).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }

  return `${mm}:${ss}`;
}

export function useCountdownFromNow(
  targetDate: string,
  onFinish?: () => void,
): string {
  const [display, setDisplay] = useState<string>(() => {
    if (!targetDate) return '';
    return formatSeconds(getRemainingSeconds(targetDate));
  });

  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    if (!targetDate) {
      setDisplay('');
      return;
    }

    const remaining = getRemainingSeconds(targetDate);
    setDisplay(formatSeconds(remaining));

    if (remaining === 0) {
      return;
    }

    const intervalId = setInterval(() => {
      const secondsLeft = getRemainingSeconds(targetDate);
      const formatted = formatSeconds(secondsLeft);
      setDisplay(formatted);

      if (secondsLeft === 0) {
        clearInterval(intervalId);
        onFinishRef.current?.();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [targetDate]);

  return display;
}